import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, TrendingUp, Clock, BarChart3, Shield, Bot } from 'lucide-react';

const strategies = [
  {
    name: 'Scalper',
    command: '/scalper',
    timeframe: '1m - 5m',
    session: 'London & NY Session',
    description: 'EMA21 + VWAP + RSI + HTF Supertrend Bias. Analisis chart cepat untuk entry scalping.',
    icon: Zap,
    color: 'from-yellow-500/20 to-transparent',
    borderColor: 'hover:border-yellow-500/50',
    tabColor: 'bg-yellow-500',
    tabActiveClass: 'border-yellow-500 text-yellow-400',
    telegram: '/images/social-proof/v2-scalper-telegram.png',
  },
  {
    name: 'Intraday',
    command: '/intraday',
    timeframe: '5m - 15m',
    session: 'London & NY Session',
    description: 'EMA21 + VWAP + RSI + HTF Supertrend Bias. Setup intraday dengan konfirmasi multi-timeframe.',
    icon: TrendingUp,
    color: 'from-blue-500/20 to-transparent',
    borderColor: 'hover:border-blue-500/50',
    tabColor: 'bg-blue-500',
    tabActiveClass: 'border-blue-500 text-blue-400',
    telegram: '/images/social-proof/v2-intraday-telegram.png',
  },
  {
    name: 'Swing',
    command: '/swing',
    timeframe: '4H - Daily',
    session: 'HTF Bias',
    description: 'EMA21 + VWAP + RSI + HTF Supertrend Bias. Analisis swing untuk posisi jangka menengah.',
    icon: Clock,
    color: 'from-purple-500/20 to-transparent',
    borderColor: 'hover:border-purple-500/50',
    tabColor: 'bg-purple-500',
    tabActiveClass: 'border-purple-500 text-purple-400',
    telegram: '/images/social-proof/v2-swing-telegram.png',
  },
];

const features = [
  {
    icon: BarChart3,
    title: 'Analisis Chart Real-Time',
    text: 'Bot menganalisis chart XAUUSD langsung dan memberikan setup berdasarkan indikator teknikal.',
  },
  {
    icon: Shield,
    title: 'Risk Management',
    text: 'Setiap analisis mencakup level SL/TP dan ukuran risiko yang sesuai strategi.',
  },
  {
    icon: Bot,
    title: 'AI-Powered',
    text: 'Didukung AI untuk membaca pattern, confluence zone, dan bias multi-timeframe.',
  },
];

const BotFeatures = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <section
      className="py-24 md:py-32 relative bg-tc-surface/30"
      data-testid="bot-features-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-tc-primary/10 border border-tc-primary/20 mb-6">
            <Bot className="w-4 h-4 text-tc-primary" />
            <span className="text-sm text-tc-primary font-medium">BOT AI TELEGRAM</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
            3 strategi, 1 perintah
          </h2>
          <p className="text-xl text-tc-text-secondary max-w-2xl mx-auto">
            Ketik perintah di Telegram, terima analisis chart XAUUSD sesuai strategi Anda
          </p>
        </motion.div>

        {/* Strategy Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {strategies.map((strat, idx) => (
            <motion.div
              key={strat.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.15 }}
              className="group"
            >
              <div className={`glass-card rounded-2xl p-8 h-full relative overflow-hidden border border-tc-border ${strat.borderColor} transition-colors`}>
                <div className={`absolute inset-0 bg-gradient-to-br ${strat.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                <div className="relative">
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-12 h-12 rounded-xl bg-tc-primary/10 flex items-center justify-center group-hover:bg-tc-primary/20 transition-colors">
                      <strat.icon className="w-6 h-6 text-tc-primary" />
                    </div>
                    <span className="font-mono text-tc-secondary text-lg font-bold">{strat.command}</span>
                  </div>
                  <h3 className="text-2xl font-heading font-bold text-white mb-2">{strat.name}</h3>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-sm text-tc-primary font-mono bg-tc-primary/10 px-2 py-1 rounded">{strat.timeframe}</span>
                    <span className="text-sm text-tc-text-secondary">{strat.session}</span>
                  </div>
                  <p className="text-tc-text-secondary leading-relaxed">{strat.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Social Proof — Real Bot Output */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="text-center text-sm text-tc-text-secondary uppercase tracking-wider mb-8 font-medium">
            Output sebenar dari bot AI kami
          </p>

          {/* Strategy Tabs */}
          <div className="flex justify-center gap-2 mb-8">
            {strategies.map((strat, idx) => (
              <button
                key={strat.name}
                onClick={() => setActiveTab(idx)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 border ${
                  activeTab === idx
                    ? `${strat.tabActiveClass} border-current bg-white/5`
                    : 'border-tc-border text-tc-text-secondary hover:text-white hover:border-tc-border-hover'
                }`}
              >
                <strat.icon className="w-4 h-4" />
                {strat.name}
              </button>
            ))}
          </div>

          {/* Screenshot Display */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
              className="max-w-6xl mx-auto"
            >
              <div className="max-w-2xl mx-auto">
                <div className="glass-card rounded-2xl p-3 border border-tc-border">
                  <div className="flex items-center gap-2 px-3 py-2 mb-2">
                    <div className={`w-2 h-2 rounded-full ${strategies[activeTab].tabColor}`} />
                    <span className="text-xs text-tc-text-secondary font-medium uppercase tracking-wider">
                      Telegram — {strategies[activeTab].command}
                    </span>
                  </div>
                  <img
                    src={strategies[activeTab].telegram}
                    alt={`${strategies[activeTab].name} analysis output`}
                    className="w-full rounded-xl"
                    loading="lazy"
                  />
                </div>
              </div>

              <p className="text-center text-xs text-tc-text-secondary mt-4">
                Screenshot sebenar dari bot. Bukan mockup.
              </p>
            </motion.div>
          </AnimatePresence>
        </motion.div>

        {/* Bot Features */}
        <div className="grid md:grid-cols-3 gap-8">
          {features.map((feat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-tc-primary/10 flex items-center justify-center flex-shrink-0">
                  <feat.icon className="w-5 h-5 text-tc-primary" />
                </div>
                <div>
                  <h4 className="text-white font-heading font-semibold mb-1">{feat.title}</h4>
                  <p className="text-tc-text-secondary text-sm leading-relaxed">{feat.text}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BotFeatures;
